import { apiService } from './ApiService';

// ---- Response types (mirror catax_ai/routes/cost.py) --------------------- //
export interface CostHeadline {
  total_usd: number;
  llm_calls: number;
  orders: number;
  searches: number;
  avg_usd_per_order: number;
  avg_usd_per_search: number;
  priority_usd: number;
  standard_usd: number;
}

export interface CostBreakdownRow {
  agent: string | null;
  model: string | null;
  tier: string | null;
  calls: number;
  cost_usd: number;
}

export interface CostDailyPoint {
  day: string;
  cost_usd: number;
  priority_usd: number;
}

export interface CostSummary {
  headline: CostHeadline;
  breakdown: CostBreakdownRow[];
  daily: CostDailyPoint[];
}

export interface OrderCostRow {
  order_id: number;
  order_name: string | null;
  status: string | null;
  source: string | null;
  vin: string | null;
  buyer: string | null;
  org_id: string | null;
  created_at: string | null;
  llm_calls: number;
  order_cost_usd: number;
  priority_cost_usd: number;
  input_tok: number;
  cached_tok: number;
  output_tok: number;
  last_activity: string | null;
  has_cost: boolean;
}

export interface SearchCostRow {
  search_id: string;
  order_id: number | null;
  parts: number;
  llm_calls: number;
  search_cost_usd: number;
  cost_per_part: number;
  last_activity: string | null;
}

export interface SessionCostRow {
  session_id: string;
  org_id: string | null;
  user_id: string | null;
  orders: number;
  searches: number;
  llm_calls: number;
  session_cost_usd: number;
  priority_cost_usd: number;
  input_tok: number;
  cached_tok: number;
  output_tok: number;
  last_activity: string | null;
}

export interface RawCostRow {
  ts: string | null;
  invocation_id: string | null;
  session_id: string | null;
  turn_id: string | null;
  org_id: string | null;
  user_id: string | null;
  order_id: number | null;
  search_id: string | null;
  num_parts: number;
  agent: string | null;
  model: string | null;
  tier: string | null;
  input_tok: number;
  cached_tok: number;
  output_tok: number;
  cost_usd: number;
}

export interface ModelRate { in: number; out: number; cached: number; }
export interface PricingTable { models: Record<string, ModelRate>; priority_mult: number; }

interface ScopeParams {
  days?: number;
  organization_id?: number;
  all?: boolean;
  pricing?: PricingTable;   // UI-edited prices; serialized into the request
}

// Flatten an optional PricingTable into the wire params the backend expects.
function withPricing<T extends ScopeParams>(params: T) {
  const { pricing, ...rest } = params as any;
  if (!pricing) return rest;
  return { ...rest, pricing: JSON.stringify(pricing.models), priority_mult: pricing.priority_mult };
}

class CostService {
  async getDefaultPricing(): Promise<PricingTable> {
    const res = await apiService.get<PricingTable>('/api/admin/llm-cost/pricing');
    return res.data;
  }

  async getSummary(params: ScopeParams = {}): Promise<CostSummary> {
    const res = await apiService.get<CostSummary>('/api/admin/llm-cost/summary', { params: withPricing(params) });
    return res.data;
  }

  async getOrders(
    params: ScopeParams & { limit?: number; offset?: number; status?: string } = {},
  ): Promise<{ rows: OrderCostRow[]; total_count: number }> {
    const res = await apiService.get<{ rows: OrderCostRow[]; total_count?: number }>('/api/admin/llm-cost/orders', { params: withPricing(params) });
    return { rows: res.data.rows, total_count: res.data.total_count ?? res.data.rows.length };
  }

  async getSearches(params: ScopeParams & { limit?: number; offset?: number } = {}): Promise<SearchCostRow[]> {
    const res = await apiService.get<{ rows: SearchCostRow[] }>('/api/admin/llm-cost/searches', { params: withPricing(params) });
    return res.data.rows;
  }

  async getSessions(params: ScopeParams & { limit?: number; offset?: number } = {}): Promise<SessionCostRow[]> {
    const res = await apiService.get<{ rows: SessionCostRow[] }>('/api/admin/llm-cost/sessions', { params: withPricing(params) });
    return res.data.rows;
  }

  async getRows(
    params: ScopeParams & { limit?: number; offset?: number; order_id?: number; search_id?: string; session_id?: string } = {},
  ): Promise<RawCostRow[]> {
    const res = await apiService.get<{ rows: RawCostRow[] }>('/api/admin/llm-cost/rows', { params: withPricing(params) });
    return res.data.rows;
  }
}

export const costService = new CostService();
export default CostService;
